import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Info } from 'lucide-react';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useCreatePlanPaiement } from '@/hooks/queries/usePlansPaiement';
import type { FactureRecouvrementRow } from '@/hooks/queries/useRecouvrementEntreprises';
import { formatDA } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { PlanPaiementFrequence } from '@/lib/database.types';

// Sprint 6.9.15 — Wrapper minimal du formulaire plans_paiement, pré-rempli depuis
// une facture Recouvrement. Évite de dupliquer la logique de PlanPaiementFormDialog
// (sens/tier/activité/facture_ref viennent de la facture).

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  facture: FactureRecouvrementRow | null;
  onSaved: () => void;
}

export function PlanPaiementDepuisFactureDialog({
  open,
  onOpenChange,
  facture,
  onSaved,
}: Props): JSX.Element {
  const create = useCreatePlanPaiement();
  const today = new Date().toISOString().slice(0, 10);
  const reste = facture ? Math.max(0, facture.montant_ttc - facture.montant_encaisse) : 0;

  const [montant, setMontant] = useState('0');
  const [nbTranches, setNbTranches] = useState('3');
  const [datePremiere, setDatePremiere] = useState(today);
  const [frequence, setFrequence] = useState<PlanPaiementFrequence>('mensuelle');
  const [intervalle, setIntervalle] = useState('30');
  const [observation, setObservation] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !facture) return;
    setMontant(String(reste));
    setNbTranches('3');
    setDatePremiere(today);
    setFrequence('mensuelle');
    setIntervalle('30');
    setObservation(`Plan échelonné facture ${facture.reference_facture}`);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facture?.id]);

  const m = Number(montant);
  const n = Number(nbTranches);
  const tranchePreview = n > 0 && m > 0 ? Math.round((m / n) * 100) / 100 : 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!facture) return;
    if (!facture.tier_id) return setError("Tier manquant sur la facture — édite-la d'abord.");
    if (!(m > 0)) return setError('Montant invalide');
    if (n < 2 || n > 60) return setError('Nombre de tranches entre 2 et 60');
    if (m > reste + 0.01)
      return setError(`Montant supérieur au reste à encaisser (${formatDA(reste)})`);

    try {
      await create.mutateAsync({
        entreprise_id: facture.entreprise_id,
        tier_id: facture.tier_id,
        sens: 'a_encaisser',
        activite_id: facture.activite_id,
        facture_ref: facture.reference_facture,
        motif: `Plan facture ${facture.reference_facture}`,
        montant_total: m,
        nb_echeances: n,
        date_premiere: datePremiere,
        frequence,
        intervalle_jours: frequence === 'sur_mesure' ? Number(intervalle) : null,
        observation: observation || null,
        recouvrement_id: facture.id,
      });
      toast.success(`Plan créé — ${n} tranches générées`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  if (!facture) {
    return (
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Plan de paiement"
        onSubmit={(e) => e.preventDefault()}
        submitLabel="Fermer"
        size="md"
      >
        <span />
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Créer un plan de paiement"
      description={`Échelonner la facture ${facture.reference_facture} en plusieurs tranches.`}
      onSubmit={handleSubmit}
      isSubmitting={create.isPending}
      submitLabel="Créer le plan"
      size="md"
    >
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Reste à encaisser : <strong>{formatDA(reste)}</strong>. Les tranches seront générées
          automatiquement comme échéances liées au plan (vue Plans de paiement).
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          label="Montant à échelonner"
          type="number"
          step="0.01"
          min="0.01"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          required
          hint={
            tranchePreview > 0
              ? `≈ ${formatDA(tranchePreview)} / tranche`
              : `Max ${formatDA(reste)}`
          }
        />
        <FormField
          label="Nombre de tranches"
          type="number"
          step="1"
          min="2"
          max="60"
          value={nbTranches}
          onChange={(e) => setNbTranches(e.target.value)}
          required
        />
        <FormField
          label="Date 1ère tranche"
          type="date"
          value={datePremiere}
          onChange={(e) => setDatePremiere(e.target.value)}
          required
        />
        <FormSelect
          label="Fréquence"
          value={frequence}
          onChange={(v) => setFrequence(v as PlanPaiementFrequence)}
          options={(
            [
              'hebdomadaire',
              'bimensuelle',
              'mensuelle',
              'trimestrielle',
              'sur_mesure',
            ] as PlanPaiementFrequence[]
          ).map((f) => ({ value: f, label: t(`echeances.plans.frequence.${f}`) }))}
        />
        {frequence === 'sur_mesure' && (
          <FormField
            label="Intervalle (jours)"
            type="number"
            step="1"
            min="1"
            value={intervalle}
            onChange={(e) => setIntervalle(e.target.value)}
            required
          />
        )}
        <FormTextarea
          label="Observation"
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          fullWidth
          rows={2}
        />
      </div>

      <div className="rounded-md border border-violet-200 bg-violet-50/60 p-2 text-[11px] text-violet-900">
        <CalendarClock className="mr-1 inline h-3 w-3" />
        Les tranches générées apparaîtront dans <strong>Échéances → Plans de paiement</strong> et
        dans la fiche tier.
      </div>
    </FormDialog>
  );
}
